/**
 * The app: which page the address asks for, who is signed in, and what every page shares (the look
 * following the device, the connection, the prompt when a new version is ready, the sign-in sheet
 * when a session ends, the toasts).
 */

import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { api } from '../api';
import { routeOf, useRoute } from '../router';
import { session, useSession } from '../state/session';
import { resolveTheme, useVenue, venue } from '../state/store';
import { EditorPage } from './pages/Editor';
import { EventPage } from './pages/Event';
import { EventsPage } from './pages/Events';
import { PeoplePage } from './pages/People';
import { Link } from './pages/Shell';
import { SignInPage, SignInSheet, WelcomePage } from './pages/SignIn';
import { ViewPage } from './pages/View';
import { NoWebGL, Toasts } from './Panels';

function useThemeSync() {
  const themePref = useVenue((s) => s.themePref);
  const theme = useVenue((s) => s.theme);
  const set = useVenue((s) => s.set);
  useEffect(() => {
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const follow = () => set({ theme: resolveTheme(themePref) });
    follow();
    mq.addEventListener('change', follow);
    return () => mq.removeEventListener('change', follow);
  }, [themePref, set]);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    for (const m of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
      m.content = theme === 'dark' ? '#121309' : '#F1F2EF';
      m.removeAttribute('media');
    }
  }, [theme]);
}

function useOnline() {
  const set = useVenue((s) => s.set);
  useEffect(() => {
    const up = () => set({ online: true });
    const down = () => set({ online: false });
    addEventListener('online', up);
    addEventListener('offline', down);
    return () => {
      removeEventListener('online', up);
      removeEventListener('offline', down);
    };
  }, [set]);
}

/** Who is signed in: the server's answer, or (with no connection) who was signed in on this device. */
function useWhoAmI() {
  useEffect(() => {
    api
      .me()
      .then(({ me }) => (me ? session().signedIn(me) : session().signedOut()))
      .catch(() => {
        // no connection, or the server isn't answering: whoever was signed in here carries on with what the device saved
        useSession.setState({ status: session().me ? 'in' : 'out' });
      });
  }, []);
}

function Missing() {
  return (
    <div className="message">
      <div className="panel card">
        <h1>There’s nothing at this address</h1>
        <p>It may have been mistyped, or the page moved.</p>
        <Link to="/" className="primary">
          Go to the events
        </Link>
      </div>
    </div>
  );
}

export function Root({ webgl }: { webgl: boolean }) {
  const path = useRoute((s) => s.path);
  const status = useSession((s) => s.status);
  const route = routeOf(path);

  useThemeSync();
  useOnline();
  useWhoAmI();

  const { updateServiceWorker } = useRegisterSW({
    onNeedRefresh() {
      venue().toast('A new version of the app is ready.', { action: { label: 'Reload', run: () => void updateServiceWorker(true) }, ms: 0 });
    },
  });

  useEffect(() => {
    requestAnimationFrame(() => venue().mark('shell'));
    const tab = (e: KeyboardEvent) => e.key === 'Tab' && document.body.setAttribute('data-tabbing', '');
    const point = () => document.body.removeAttribute('data-tabbing');
    addEventListener('keydown', tab, true);
    addEventListener('pointerdown', point, true);
    return () => {
      removeEventListener('keydown', tab, true);
      removeEventListener('pointerdown', point, true);
    };
  }, []);

  useEffect(() => {
    document.body.dataset.page = route.page;
  }, [route.page]);

  let page;
  if (route.page === 'view') page = webgl ? <ViewPage token={route.token} /> : <NoWebGL />;
  else if (route.page === 'welcome') page = <WelcomePage />;
  else if (status === 'unknown') page = <div className="splash" aria-busy="true" />;
  else if (status === 'out') page = <SignInPage />;
  else if (route.page === 'events') page = <EventsPage />;
  else if (route.page === 'event') page = <EventPage key={route.id} id={route.id} />;
  else if (route.page === 'layout') page = webgl ? <EditorPage key={route.id} id={route.id} /> : <NoWebGL />;
  else if (route.page === 'people') page = <PeoplePage />;
  else page = <Missing />;

  return (
    <>
      {page}
      {route.page !== 'view' && route.page !== 'welcome' && <SignInSheet />}
      <Toasts />
    </>
  );
}
